import React, { useCallback, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { SxProps, Theme } from "@mui/material/styles";
import { LightTooltip } from "../../../LightTooltip";
import { FaiByDepartmentsAndRolesInput } from "./FaiByDepartmentsAndRolesInput";

interface FaiFilterSectionProps {
  disabled?: boolean;
  selectedIds?: string[];
  onFaiSelect: (
    selectedDepartmentIds: string[],
    selectedRoleIds: string[]
  ) => void;
  id: string;
  sx?: SxProps<Theme>;
}

const TOOLTIP_TEXT =
  "Functional Area Intelligence (FAI) allows you to filter by product category presence within specific departments. Certain departments, such as Engineering, allow you to refine further to look for product category usage by specific roles.";

/**
 * Reusable FAI filter section component
 * Encapsulates the checkbox + tooltip + conditional FAI input pattern
 */
export const FaiFilterSection: React.FC<FaiFilterSectionProps> = React.memo(
  ({ disabled = false, selectedIds = [], onFaiSelect, id, sx }) => {
    // Need local state to track user intent to enable FAI filter
    // This allows users to check the box before making selections
    const [faiEnabled, setFaiEnabled] = useState(selectedIds.length > 0);

    // Only auto-enable when selections are added, don't auto-disable when cleared
    useEffect(() => {
      if (selectedIds.length > 0) {
        setFaiEnabled(true);
      }

      if (selectedIds.length === 0 && disabled) {
        setFaiEnabled(false);
      }
    }, [selectedIds.length, disabled]);

    const handleFaiToggle = useCallback(() => {
      setFaiEnabled((prev) => {
        const newEnabled = !prev;

        // Clear FAI selection when disabling
        if (!newEnabled) {
          onFaiSelect([], []);
        }

        return newEnabled;
      });
    }, [onFaiSelect]);

    return (
      <Box sx={sx}>
        {/* FAI Checkbox */}
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            sx={{ mr: 1 }}
            control={
              <Checkbox
                checked={faiEnabled}
                onChange={handleFaiToggle}
                disabled={disabled}
                size="small"
                data-testid="scoring-fai-enable-checkbox-btn"
              />
            }
            label="Refine with Functional Area Intelligence"
          />
          <LightTooltip title={TOOLTIP_TEXT} placement="top">
            <InfoOutlineIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          </LightTooltip>
        </Stack>

        {/* FAI Filter */}
        {faiEnabled && !disabled && (
          <Box my={1}>
            <FaiByDepartmentsAndRolesInput
              id={id}
              selectedItems={selectedIds}
              onFaiSelect={onFaiSelect}
              showDoneButton
            />
          </Box>
        )}
      </Box>
    );
  }
);
